import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import AdminLayout from "@/components/AdminLayout";
import { SellerBalanceById } from "@/components/SellerBalance";
import { PageHeader, DataTable, StatusBadge, EmptyState, StatCard } from "@/components/ui-system";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Search, PackageSearch, RotateCcw, Wallet, TrendingUp, Receipt, Plus, Loader2, Truck, CheckCircle2, XCircle, AlertTriangle, Printer, Download, History, RefreshCw } from "lucide-react";
import { LABEL_SIZES } from "@/components/b2c/ShippingLabelDownload";
import { formatToIST } from "@/utils/dateUtils";
import { useLocalStorageState } from "@/hooks/useLocalStorageState";
import { can } from "@/lib/permissions";
import ChannelBadge from "@/components/b2c/ChannelBadge";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

// Lifecycle tab -> status group key (matches the admin service's TAB_STATUS_MAP). `null` = no status filter.
const TABS = [
  { id: "new", label: "New", group: "new" },
  { id: "ready", label: "Ready to Ship", group: "ready" },
  { id: "pickup", label: "Pickup & Manifest", group: "pickup" },
  { id: "transit", label: "In Transit", group: "transit" },
  { id: "delivered", label: "Delivered", group: "delivered" },
  { id: "rto", label: "RTO", group: "rto" },
  { id: "all", label: "All", group: null },
];

const PAGE_SIZE = 50;
// Bulk label download needs at least this many printable orders (a single label
// is downloaded from the order page). Mirrors backend bulk_label_service.MIN_BULK_LABELS.
const MIN_BULK_LABELS = 2;
const ALL = "all"; // Radix Select forbids value="" — use a sentinel for the "no filter" option.

// The landing view defaults to a recent window so the cross-seller counts/summary
// aggregations stay selective at scale (5L orders/day) instead of scanning the
// whole b2c_orders collection. Admins widen "From" (or clear it) to see older orders.
const DEFAULT_WINDOW_DAYS = 30;
const daysAgoISO = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10); // YYYY-MM-DD
};

const inr = (n) =>
  n == null
    ? "-"
    : `₹${Number(n).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const errDetail = (error, fallback) => {
  const d = error?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg;
  return error?.response?.data?.message || fallback;
};

const B2COrders = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [activeTab, setActiveTab] = useLocalStorageState("admin-b2c-orders:tab", "all");
  const [sellerId, setSellerId] = useLocalStorageState("admin-b2c-orders:seller", ALL);
  const [aggregator, setAggregator] = useLocalStorageState("admin-b2c-orders:aggregator", ALL);
  const [paymentMode, setPaymentMode] = useLocalStorageState("admin-b2c-orders:payment", ALL);
  const [dateFrom, setDateFrom] = useLocalStorageState("admin-b2c-orders:date_from", daysAgoISO(DEFAULT_WINDOW_DAYS));
  const [dateTo, setDateTo] = useLocalStorageState("admin-b2c-orders:date_to", "");
  const [search, setSearch] = useLocalStorageState("admin-b2c-orders:search", "");
  const [searchInput, setSearchInput] = useState(search);
  const [page, setPage] = useState(1);

  const [data, setData] = useState({
    items: [],
    total: 0,
    page: 1,
    page_size: PAGE_SIZE,
    counts: {},
    summary: {},
  });
  const [loading, setLoading] = useState(true);

  const [options, setOptions] = useState({ sellers: [], aggregators: [] });

  const abortRef = useRef(null);

  // --- Bulk ship (New tab multi-select) ------------------------------------
  const canShip = can(user, "b2c.orders:ship");
  const [selectedIds, setSelectedIds] = useState(() => new Set());   // explicit mode
  const [selectAllMatching, setSelectAllMatching] = useState(false); // filter mode
  const [excludedIds, setExcludedIds] = useState(() => new Set());   // filter-mode de-selections
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkPreview, setBulkPreview] = useState(null);
  const [bulkJob, setBulkJob] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const bulkPollRef = useRef(null);

  // --- Bulk shipping labels (every tab except New — those orders have an AWB) ---
  const [labelOpen, setLabelOpen] = useState(false);
  const [labelSize, setLabelSize] = useLocalStorageState("admin-b2c-orders:label_size", "4x6");
  const [labelPreview, setLabelPreview] = useState(null);
  const [labelJob, setLabelJob] = useState(null);
  const [labelBusy, setLabelBusy] = useState(false);
  const labelPollRef = useRef(null);

  // Recently generated bulk-label files (header "Recent labels" dialog).
  const [recentOpen, setRecentOpen] = useState(false);
  const [recentJobs, setRecentJobs] = useState([]);
  const [recentLoading, setRecentLoading] = useState(false);

  // Sync tab from a ?status_group= deep link (dashboard click-through) once on mount.
  const didSyncDeepLink = useRef(false);
  useEffect(() => {
    if (didSyncDeepLink.current) return;
    didSyncDeepLink.current = true;
    const grp = searchParams.get("status_group") || searchParams.get("tab");
    if (grp && TABS.some((t) => t.id === grp)) setActiveTab(grp);
  }, [searchParams, setActiveTab]);

  // Load the seller + aggregator dropdown options once.
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/b2c/admin/orders/filter-options`)
      .then((r) => {
        if (!cancelled) setOptions({ sellers: r.data?.sellers || [], aggregators: r.data?.aggregators || [] });
      })
      .catch(() => {
        if (!cancelled) setOptions({ sellers: [], aggregators: [] });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Debounce the search input into the persisted search term.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput, setSearch]);

  const tabGroup = (TABS.find((t) => t.id === activeTab) || TABS[TABS.length - 1]).group;

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (tabGroup) params.status_group = tabGroup;
      if (sellerId && sellerId !== ALL) params.seller_id = sellerId;
      if (aggregator && aggregator !== ALL) params.aggregator = aggregator;
      if (paymentMode && paymentMode !== ALL) params.payment_mode = paymentMode;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (search) params.search = search;
      const { data: res } = await axios.get(`${API}/b2c/admin/orders`, { params, signal: controller.signal });
      setData({
        items: res.items || [],
        total: res.total || 0,
        page: res.page || 1,
        page_size: res.page_size || PAGE_SIZE,
        counts: res.counts || {},
        summary: res.summary || {},
      });
    } catch (e) {
      if (axios.isCancel?.(e) || e?.code === "ERR_CANCELED" || e?.name === "CanceledError") return;
      toast.error(errDetail(e, "Failed to load B2C orders"));
    } finally {
      setLoading(false);
    }
  }, [page, tabGroup, sellerId, aggregator, paymentMode, dateFrom, dateTo, search]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Reset to page 1 whenever any filter (tab / seller / aggregator / payment / dates / search) changes.
  useEffect(() => {
    setPage(1);
  }, [activeTab, sellerId, aggregator, paymentMode, dateFrom, dateTo, search]);

  // A selection is only meaningful for the current tab + filter set — drop it when
  // the tab or any filter changes (page changes KEEP the selection so an admin can
  // tick rows across pages).
  useEffect(() => {
    setSelectedIds(new Set());
    setSelectAllMatching(false);
    setExcludedIds(new Set());
  }, [activeTab, sellerId, aggregator, paymentMode, dateFrom, dateTo, search]);

  // Stop polling a bulk-ship / bulk-label job if the page unmounts mid-run.
  useEffect(() => () => {
    if (bulkPollRef.current) clearInterval(bulkPollRef.current);
    if (labelPollRef.current) clearInterval(labelPollRef.current);
  }, []);

  const resetFilters = () => {
    setSellerId(ALL);
    setAggregator(ALL);
    setPaymentMode(ALL);
    // Reset to the default recent window (not all-time) to keep the view selective.
    setDateFrom(daysAgoISO(DEFAULT_WINDOW_DAYS));
    setDateTo("");
    setSearch("");
    setSearchInput("");
  };

  const hasFilters =
    (sellerId && sellerId !== ALL) ||
    (aggregator && aggregator !== ALL) ||
    (paymentMode && paymentMode !== ALL) ||
    !!dateFrom ||
    !!dateTo ||
    !!search;

  const tabCount = (id) => {
    const counts = data.counts || {};
    if (id === "all") return counts.all ?? data.total;
    return counts[id];
  };

  const summary = data.summary || {};
  const marginTotal = summary.margin_total;
  const marginAccent = marginTotal == null ? "neutral" : marginTotal >= 0 ? "success" : "danger";
  const marginPctLine =
    summary.margin_pct != null ? `${Number(summary.margin_pct).toLocaleString("en-IN")}% of revenue` : undefined;

  // --- selection derivation + handlers -------------------------------------
  // New tab selects for bulk ship; every other tab selects for bulk labels.
  const isNewTab = activeTab === "new";
  const isLabelTab = !isNewTab;
  const selectable = isNewTab ? canShip : isLabelTab;
  const pageIds = data.items.map((o) => o.id);
  const isRowSelected = (id) => (selectAllMatching ? !excludedIds.has(id) : selectedIds.has(id));
  const selectedCount = selectAllMatching
    ? Math.max(0, (data.total || 0) - excludedIds.size)
    : selectedIds.size;
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => isRowSelected(id));
  const anySelected = selectAllMatching || selectedIds.size > 0;
  const moreThanPage = (data.total || 0) > data.items.length;

  const toggleRow = (id) => {
    const target = selectAllMatching ? setExcludedIds : setSelectedIds;
    target((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const togglePage = () => {
    if (selectAllMatching) {
      // exclusion model: selecting a page = un-exclude it; deselecting = exclude it
      setExcludedIds((prev) => {
        const next = new Set(prev);
        pageIds.forEach((id) => (allPageSelected ? next.add(id) : next.delete(id)));
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        pageIds.forEach((id) => (allPageSelected ? next.delete(id) : next.add(id)));
        return next;
      });
    }
  };
  const selectAllFilter = () => {
    setSelectAllMatching(true);
    setExcludedIds(new Set());
    setSelectedIds(new Set());
  };
  const clearSelection = () => {
    setSelectAllMatching(false);
    setExcludedIds(new Set());
    setSelectedIds(new Set());
  };

  const buildScope = (extraFilters = {}) => {
    const filters = { ...extraFilters };
    if (sellerId && sellerId !== ALL) filters.seller_id = sellerId;
    if (aggregator && aggregator !== ALL) filters.aggregator = aggregator;
    if (paymentMode && paymentMode !== ALL) filters.payment_mode = paymentMode;
    if (dateFrom) filters.date_from = dateFrom;
    if (dateTo) filters.date_to = dateTo;
    if (search) filters.search = search;
    return selectAllMatching
      ? { select_all_matching: true, exclude_ids: [...excludedIds], ...filters }
      : { order_ids: [...selectedIds] };
  };

  const openBulkShip = async () => {
    setBulkOpen(true);
    setBulkPreview(null);
    setBulkJob(null);
    setBulkBusy(true);
    try {
      const { data: p } = await axios.post(`${API}/b2c/admin/orders/bulk-ship/preview`, buildScope());
      setBulkPreview(p);
    } catch (e) {
      toast.error(errDetail(e, "Could not preview bulk ship"));
      setBulkOpen(false);
    } finally {
      setBulkBusy(false);
    }
  };

  const confirmBulkShip = async () => {
    setBulkBusy(true);
    try {
      const { data: started } = await axios.post(`${API}/b2c/admin/orders/bulk-ship`, buildScope());
      const poll = async () => {
        try {
          const { data: job } = await axios.get(`${API}/b2c/admin/orders/bulk-ship/jobs/${started.job_id}`);
          setBulkJob(job);
          if (["COMPLETED", "FAILED", "CANCELLED"].includes(job.status)) {
            if (bulkPollRef.current) clearInterval(bulkPollRef.current);
            bulkPollRef.current = null;
            setBulkBusy(false);
            fetchOrders();
          }
        } catch {
          /* transient — keep polling */
        }
      };
      await poll();
      if (!bulkPollRef.current) bulkPollRef.current = setInterval(poll, 2500);
    } catch (e) {
      toast.error(errDetail(e, "Bulk ship failed to start"));
      setBulkBusy(false);
    }
  };

  const cancelBulkJob = async () => {
    if (!bulkJob?.id) return;
    try {
      await axios.post(`${API}/b2c/admin/orders/bulk-ship/jobs/${bulkJob.id}/cancel`);
      toast.message("Stopping after the current order…");
    } catch (e) {
      toast.error(errDetail(e, "Could not stop the run"));
    }
  };

  const closeBulk = () => {
    if (bulkPollRef.current) {
      clearInterval(bulkPollRef.current);
      bulkPollRef.current = null;
    }
    if (bulkJob && ["COMPLETED", "CANCELLED", "FAILED"].includes(bulkJob.status)) clearSelection();
    setBulkOpen(false);
    setBulkJob(null);
    setBulkPreview(null);
    setBulkBusy(false);
  };

  const fetchRecentLabels = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setRecentLoading(true);
    try {
      const { data: res } = await axios.get(`${API}/b2c/admin/orders/bulk-labels/jobs`, { params: { limit: 20 } });
      setRecentJobs(res.items || []);
    } catch (e) {
      if (!quiet) toast.error(errDetail(e, "Could not load recent label downloads"));
    } finally {
      if (!quiet) setRecentLoading(false);
    }
  }, []);

  useEffect(() => {
    if (recentOpen) fetchRecentLabels();
  }, [recentOpen, fetchRecentLabels]);

  // While the dialog is open and a run is still going, refresh quietly so its
  // progress (and then its download link) appears without a manual refresh.
  const recentHasActive = recentJobs.some((j) => !["COMPLETED", "FAILED", "CANCELLED"].includes(j.status));
  useEffect(() => {
    if (!recentOpen || !recentHasActive) return undefined;
    const t = setInterval(() => fetchRecentLabels({ quiet: true }), 4000);
    return () => clearInterval(t);
  }, [recentOpen, recentHasActive, fetchRecentLabels]);

  // Bulk labels: select-all-matching must also respect the active tab's statuses.
  const buildLabelScope = () => buildScope(tabGroup ? { status_group: tabGroup } : {});
  const labelDone = labelJob && ["COMPLETED", "FAILED", "CANCELLED"].includes(labelJob.status);

  const openBulkLabels = async () => {
    setLabelOpen(true);
    setLabelPreview(null);
    setLabelJob(null);
    setLabelBusy(true);
    try {
      const { data: p } = await axios.post(`${API}/b2c/admin/orders/bulk-labels/preview`, buildLabelScope());
      setLabelPreview(p);
    } catch (e) {
      toast.error(errDetail(e, "Could not check the selection"));
      setLabelOpen(false);
    } finally {
      setLabelBusy(false);
    }
  };

  const startBulkLabels = async () => {
    setLabelBusy(true);
    try {
      const { data: started } = await axios.post(`${API}/b2c/admin/orders/bulk-labels`, {
        ...buildLabelScope(),
        size: labelSize,
      });
      const poll = async () => {
        try {
          const { data: job } = await axios.get(`${API}/b2c/admin/orders/bulk-labels/jobs/${started.job_id}`);
          setLabelJob(job);
          if (["COMPLETED", "FAILED", "CANCELLED"].includes(job.status)) {
            if (labelPollRef.current) clearInterval(labelPollRef.current);
            labelPollRef.current = null;
            setLabelBusy(false);
          }
        } catch {
          /* transient — keep polling */
        }
      };
      await poll();
      if (!labelPollRef.current) labelPollRef.current = setInterval(poll, 2500);
    } catch (e) {
      toast.error(errDetail(e, "Could not start the label download"));
      setLabelBusy(false);
    }
  };

  const cancelBulkLabels = async () => {
    if (!labelJob?.id) return;
    try {
      await axios.post(`${API}/b2c/admin/orders/bulk-labels/jobs/${labelJob.id}/cancel`);
      toast.message("Stopping — labels generated so far will still be packaged…");
    } catch (e) {
      toast.error(errDetail(e, "Could not stop the run"));
    }
  };

  const closeLabels = () => {
    if (labelPollRef.current) {
      clearInterval(labelPollRef.current);
      labelPollRef.current = null;
    }
    if (labelDone && labelJob.status === "COMPLETED") clearSelection();
    setLabelOpen(false);
    setLabelJob(null);
    setLabelPreview(null);
    setLabelBusy(false);
  };

  const columns = [
    {
      key: "order",
      header: "Order #",
      primary: true,
      cell: (o) => (
        <div className="min-w-0">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/admin/b2c/orders/${o.id}`);
            }}
            className="text-sm font-medium text-brand hover:underline"
            data-testid={`admin-b2c-order-link-${o.id}`}
          >
            {o.order_number}
          </button>
          {o.reference_number && (
            <div className="text-xs text-slate-400 truncate" title={o.reference_number}>
              Ref: {o.reference_number}
            </div>
          )}
        </div>
      ),
    },
    {
      key: "date",
      header: "Date",
      hideOnMobile: true,
      cell: (o) => (
        <div className="whitespace-nowrap text-sm text-slate-600">
          <div>{formatToIST(o.created_at, false)}</div>
          <div className="text-xs text-slate-500">{formatToIST(o.created_at).split(", ")[1]}</div>
        </div>
      ),
    },
    {
      key: "seller",
      header: "Seller",
      cell: (o) => (
        <div>
          <span className="text-sm font-medium text-slate-800">{o.seller_brand_name || o.seller_id || "-"}</span>
          <SellerBalanceById sellerId={o.seller_id} />
        </div>
      ),
    },
    {
      key: "pickup_location",
      header: "Pickup Location",
      hideOnMobile: true,
      cell: (o) =>
        o.pickup_location_name ? (
          <span
            className="text-sm text-slate-700 border-b border-dotted border-slate-300 cursor-help"
            title={o.pickup_location_address || o.pickup_location_name}
          >
            {o.pickup_location_name}
          </span>
        ) : (
          <span className="text-sm text-slate-400">-</span>
        ),
    },
    {
      key: "customer",
      header: "Customer",
      cell: (o) => (
        <div className="text-sm">
          <div className="font-medium text-slate-800">{o.buyer?.name || "-"}</div>
          <div className="text-xs text-slate-500">
            {[o.buyer?.city, o.buyer?.pincode].filter(Boolean).join(", ")}
          </div>
        </div>
      ),
    },
    {
      key: "channel",
      header: "Channel",
      hideOnMobile: true,
      cell: (o) =>
        o.channel_name ? (
          <ChannelBadge name={o.channel_name} logoUrl={o.channel_logo_url} />
        ) : (
          <span className="text-sm text-slate-400">{o.source === "bulk" ? "Bulk" : "Direct"}</span>
        ),
    },
    {
      key: "payment",
      header: "Payment",
      cell: (o) => (
        <div className="text-sm">
          <span className={o.payment_mode === "cod" ? "font-medium text-amber-700" : "font-medium text-slate-700"}>
            {o.payment_mode === "cod" ? "COD" : "Prepaid"}
          </span>
          <div className="text-xs text-slate-500 tabular-nums">
            {o.payment_mode === "cod" ? inr(o.cod_amount) : inr(o.order_value)}
          </div>
        </div>
      ),
    },
    {
      key: "courier",
      header: "Courier",
      hideOnMobile: true,
            cell: (o) => (
        <div className="min-w-0">
          <span className="text-sm text-slate-700">{o.aggregator_courier_name || o.courier_name || o.courier_code || <span className="text-slate-400">-</span>}</span>
        </div>
      ),

    },
    {
      key: "awb",
      header: "AWB",
      hideOnMobile: true,
      cell: (o) =>
        o.awb ? (
          <span className="text-sm font-mono text-slate-700">{o.awb}</span>
        ) : o.status && !["NEW", "CANCELLED"].includes(o.status) ? (
          <span
            className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-600"
            title="Courier assigns the AWB after manifest — it will appear automatically."
          >
            <Loader2 className="h-3 w-3 animate-spin" /> In progress
          </span>
        ) : (
          <span className="text-sm text-slate-400">-</span>
        ),
    },
    {
      key: "aggregator",
      header: "Aggregator",
      hideOnMobile: true,
      cell: (o) => (
        o.aggregator ? (
          <div className="min-w-0">
            <div className="text-sm capitalize text-slate-700">{o.aggregator}</div>
            {o.aggregator_partner_id && (
              <div className="text-[10px] font-mono text-slate-400 truncate" title={o.aggregator_partner_id}>
                {o.aggregator_partner_id}
              </div>
            )}
          </div>
        ) : <span className="text-sm text-slate-400">-</span>
      ),
    },
    {
      key: "cost",
      header: "Cost",
      align: "right",
      cell: (o) => <span className="text-sm tabular-nums text-slate-700">{inr(o.cost_total)}</span>,
    },
    {
      key: "charge",
      header: "Charge",
      align: "right",
      cell: (o) => <span className="text-sm tabular-nums text-slate-700">{inr(o.seller_charge)}</span>,
    },
    {
      key: "margin",
      header: "Margin",
      align: "right",
      cell: (o) =>
        o.margin == null ? (
          <span className="text-sm tabular-nums text-slate-400">-</span>
        ) : (
          <span className={`text-sm font-medium tabular-nums ${o.margin >= 0 ? "text-green-700" : "text-red-600"}`}>
            {inr(o.margin)}
          </span>
        ),
    },
    {
      key: "status",
      header: "Status",
      cell: (o) => <StatusBadge status={o.status} kind="b2c" />,
    },
  ];

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / PAGE_SIZE));

  // Prepend a selection checkbox column: New tab (ship permission) for bulk ship,
  // every other tab for bulk shipping labels.
  const selectColumn = {
    key: "select",
    header: (
      <Checkbox
        checked={allPageSelected}
        onCheckedChange={togglePage}
        aria-label="Select all orders on this page"
        data-testid="admin-b2c-select-page"
      />
    ),
    mobileLabel: "Select",
    cell: (o) => (
      <div onClick={(e) => e.stopPropagation()} className="flex">
        <Checkbox
          checked={isRowSelected(o.id)}
          onCheckedChange={() => toggleRow(o.id)}
          aria-label={`Select ${o.order_number}`}
          data-testid={`admin-b2c-select-${o.id}`}
        />
      </div>
    ),
  };
  const tableColumns = selectable ? [selectColumn, ...columns] : columns;

  const emptyNode = (
    <EmptyState
      icon={PackageSearch}
      title="No B2C orders match these filters"
      description={
        hasFilters
          ? "Try widening the seller, aggregator, payment or date filters, or clear them to see everything."
          : "No B2C shipments have been created across any seller yet."
      }
      action={
        hasFilters ? (
          <Button type="button" variant="outline" onClick={resetFilters} data-testid="admin-b2c-empty-reset">
            <RotateCcw className="w-4 h-4 mr-2" /> Clear filters
          </Button>
        ) : undefined
      }
    />
  );

  return (
    <AdminLayout user={user} onLogout={onLogout}>
      <div className="space-y-6 w-full">
        <PageHeader
          title="B2C Orders"
          subtitle="All B2C shipments across sellers — cost, margin & fulfilment"
          actions={
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" onClick={() => setRecentOpen(true)} data-testid="admin-b2c-recent-labels">
                <History className="w-4 h-4 mr-2" /> Recent Bulk labels
              </Button>
              {can(user, "b2c.orders:create") && (
                <Button onClick={() => navigate("/admin/b2c/orders/create")} data-testid="b2c-admin-create-order">
                  <Plus className="w-4 h-4 mr-2" /> Create order
                </Button>
              )}
            </div>
          }
        />

        {/* Summary stat row (reflects the active filter) */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            label="Orders"
            value={Number(summary.order_count ?? 0).toLocaleString("en-IN")}
            icon={PackageSearch}
            accent="brand"
            loading={loading}
          />
          <StatCard
            label="Revenue"
            value={inr(summary.revenue_total ?? 0)}
            icon={Receipt}
            accent="info"
            loading={loading}
            trend="Charged to sellers"
          />
          <StatCard
            label="Aggregator Cost"
            value={inr(summary.cost_total ?? 0)}
            icon={Wallet}
            accent="warning"
            loading={loading}
            trend="Paid to aggregators"
          />
          <StatCard
            label="Margin"
            value={inr(marginTotal ?? 0)}
            icon={TrendingUp}
            accent={marginAccent}
            loading={loading}
            trend={marginPctLine}
          />
        </div>

        {/* Lifecycle tabs */}
        <div className="flex gap-2 border-b border-slate-200 bg-white px-2 sm:px-6 rounded-t-lg shadow-sm overflow-x-auto">
          {TABS.map((tab) => {
            const c = tabCount(tab.id);
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                data-testid={`admin-b2c-tab-${tab.id}`}
                className={`px-3 sm:px-5 py-3 text-xs sm:text-sm font-medium transition-colors border-b-2 whitespace-nowrap flex-shrink-0 ${
                  activeTab === tab.id
                    ? "border-brand text-brand"
                    : "border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300"
                }`}
              >
                {tab.label}
                {c != null && <span className="font-bold text-brand"> ({c})</span>}
              </button>
            );
          })}
        </div>

        {/* Filter bar */}
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-600">Seller</Label>
                <Select value={sellerId} onValueChange={setSellerId}>
                  <SelectTrigger data-testid="admin-b2c-filter-seller">
                    <SelectValue placeholder="All sellers" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>All sellers</SelectItem>
                    {options.sellers.map((s) => (
                      <SelectItem key={s.seller_id} value={s.seller_id}>
                        {s.brand_name || s.seller_id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs text-slate-600">Aggregator</Label>
                <Select value={aggregator} onValueChange={setAggregator}>
                  <SelectTrigger data-testid="admin-b2c-filter-aggregator">
                    <SelectValue placeholder="All aggregators" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>All aggregators</SelectItem>
                    {options.aggregators.map((a) => (
                      <SelectItem key={a} value={a} className="capitalize">
                        {a}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs text-slate-600">Payment</Label>
                <Select value={paymentMode} onValueChange={setPaymentMode}>
                  <SelectTrigger data-testid="admin-b2c-filter-payment">
                    <SelectValue placeholder="All payments" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>All payments</SelectItem>
                    <SelectItem value="cod">COD</SelectItem>
                    <SelectItem value="prepaid">Prepaid</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs text-slate-600" htmlFor="admin-b2c-date-from">
                  From
                </Label>
                <Input
                  id="admin-b2c-date-from"
                  type="date"
                  value={dateFrom}
                  max={dateTo || undefined}
                  onChange={(e) => setDateFrom(e.target.value)}
                  data-testid="admin-b2c-filter-date-from"
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs text-slate-600" htmlFor="admin-b2c-date-to">
                  To
                </Label>
                <Input
                  id="admin-b2c-date-to"
                  type="date"
                  value={dateTo}
                  min={dateFrom || undefined}
                  onChange={(e) => setDateTo(e.target.value)}
                  data-testid="admin-b2c-filter-date-to"
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs text-slate-600" htmlFor="admin-b2c-search">
                  Search
                </Label>
                <div className="relative">
                  <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                  <Input
                    id="admin-b2c-search"
                    placeholder="Order #, AWB or phone..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    className="pl-9"
                    data-testid="admin-b2c-orders-search"
                  />
                </div>
              </div>
            </div>

            <div className="mt-3 flex items-center justify-between gap-3">
              <p className="text-xs text-slate-500">
                Showing the last {DEFAULT_WINDOW_DAYS} days by default — clear or widen the “From” date to see older orders.
              </p>
              {hasFilters && (
                <Button type="button" variant="ghost" size="sm" onClick={resetFilters} data-testid="admin-b2c-reset-filters">
                  <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Clear filters
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Bulk action bar: ship (New tab, ship permission) / labels (other tabs) */}
        {selectable && anySelected && (
          <div className="flex flex-col gap-3 rounded-xl border border-brand/30 bg-brand/5 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm text-slate-700">
              <span className="font-semibold text-brand">{selectedCount.toLocaleString("en-IN")}</span>{" "}
              order{selectedCount === 1 ? "" : "s"} selected
              {!selectAllMatching && allPageSelected && moreThanPage && (
                <button
                  type="button"
                  onClick={selectAllFilter}
                  className="ml-2 font-medium text-brand hover:underline"
                  data-testid="admin-b2c-select-all-matching"
                >
                  Select all {(data.total || 0).toLocaleString("en-IN")} matching filters
                </button>
              )}
              {selectAllMatching && (
                <span className="ml-2 text-slate-500">(everything matching the current filters)</span>
              )}
              {isLabelTab && selectedCount < MIN_BULK_LABELS && (
                <span className="ml-2 text-slate-500" data-testid="admin-b2c-bulk-labels-min-hint">
                  — select at least {MIN_BULK_LABELS} orders to download labels in bulk (use the order page for one).
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={clearSelection} data-testid="admin-b2c-clear-selection">
                Clear
              </Button>
              {isNewTab ? (
                <Button
                  size="sm"
                  onClick={openBulkShip}
                  disabled={selectedCount === 0}
                  data-testid="admin-b2c-bulk-ship"
                >
                  <Truck className="mr-2 h-4 w-4" /> Ship {selectedCount.toLocaleString("en-IN")} order
                  {selectedCount === 1 ? "" : "s"}
                </Button>
              ) : (
                <Button
                  size="sm"
                  onClick={openBulkLabels}
                  disabled={selectedCount < MIN_BULK_LABELS}
                  data-testid="admin-b2c-bulk-labels"
                >
                  <Printer className="mr-2 h-4 w-4" /> Download labels
                </Button>
              )}
            </div>
          </div>
        )}

        <DataTable
          columns={tableColumns}
          data={data.items}
          rowKey={(o) => o.id}
          onRowClick={(o) => navigate(`/admin/b2c/orders/${o.id}`)}
          loading={loading}
          empty={emptyNode}
          pagination={
            totalPages > 1
              ? { page, pages: totalPages, total: data.total, pageSize: PAGE_SIZE, onPageChange: setPage }
              : undefined
          }
        />
      </div>

      {/* Bulk-ship preview -> progress -> results dialog */}
      <Dialog open={bulkOpen} onOpenChange={(v) => { if (!v) closeBulk(); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="h-5 w-5 text-brand" /> Bulk ship orders
            </DialogTitle>
            <DialogDescription>
              Each order ships via its seller’s courier selection (cheapest serviceable courier by
              default). The seller’s wallet is debited per order. Orders that can’t ship (e.g. no
              serviceable courier, low balance, missing details) are reported with a reason.
            </DialogDescription>
          </DialogHeader>

          {!bulkJob &&
            (bulkPreview ? (
              <div className="space-y-3 text-sm">
                <div className="rounded-lg border bg-slate-50 p-3">
                  <p>
                    <span className="font-semibold">{bulkPreview.will_process.toLocaleString("en-IN")}</span>{" "}
                    order{bulkPreview.will_process === 1 ? "" : "s"} will be shipped now.
                  </p>
                  {bulkPreview.capped_out > 0 && (
                    <p className="mt-1 flex items-start gap-1.5 text-amber-700">
                      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                      <span>
                        {bulkPreview.matched.toLocaleString("en-IN")} match, but up to {bulkPreview.cap} ship per
                        run. Re-run to ship the remaining {bulkPreview.capped_out.toLocaleString("en-IN")}.
                      </span>
                    </p>
                  )}
                </div>
                {bulkPreview.will_process === 0 && (
                  <p className="text-slate-500">
                    Nothing to ship — none of the selected orders are still in the New state.
                  </p>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2 py-4 text-sm text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" /> Checking selection…
              </div>
            ))}

          {bulkJob && (
            <div className="space-y-3 text-sm">
              <Progress value={bulkJob.progress_percentage || 0} />
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
                <span>
                  Processed <b>{bulkJob.processed_orders}/{bulkJob.total_orders}</b>
                </span>
                <span className="flex items-center gap-1 text-emerald-700">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Shipped <b>{bulkJob.shipped_orders}</b>
                </span>
                <span className="flex items-center gap-1 text-rose-700">
                  <XCircle className="h-3.5 w-3.5" /> Failed <b>{bulkJob.failed_orders}</b>
                </span>
                {bulkJob.skipped_orders > 0 && (
                  <span className="text-slate-500">
                    Skipped <b>{bulkJob.skipped_orders}</b>
                  </span>
                )}
              </div>
              <p className="text-xs capitalize text-slate-500">Status: {String(bulkJob.status).toLowerCase()}</p>
              {bulkJob.error_summary?.length > 0 && (
                <div className="max-h-40 overflow-auto rounded-lg border bg-rose-50/60 p-3">
                  <p className="mb-1 font-medium text-rose-800">Failure reasons</p>
                  <ul className="list-disc space-y-0.5 pl-4 text-xs text-rose-700">
                    {bulkJob.error_summary.map((e, i) => (
                      <li key={i}>{e}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            {!bulkJob && (
              <>
                <Button variant="outline" onClick={closeBulk} disabled={bulkBusy}>
                  Cancel
                </Button>
                <Button
                  onClick={confirmBulkShip}
                  disabled={bulkBusy || !bulkPreview || bulkPreview.will_process === 0}
                  data-testid="admin-b2c-bulk-ship-confirm"
                >
                  {bulkBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Truck className="mr-2 h-4 w-4" />}
                  Ship {bulkPreview ? bulkPreview.will_process.toLocaleString("en-IN") : ""} now
                </Button>
              </>
            )}
            {bulkJob && !["COMPLETED", "FAILED", "CANCELLED"].includes(bulkJob.status) && (
              <Button variant="outline" onClick={cancelBulkJob}>
                Stop
              </Button>
            )}
            {bulkJob && ["COMPLETED", "FAILED", "CANCELLED"].includes(bulkJob.status) && (
              <Button onClick={closeBulk} data-testid="admin-b2c-bulk-ship-done">
                Done
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk shipping labels: size + format -> progress -> download */}
      <Dialog open={labelOpen} onOpenChange={(v) => { if (!v) closeLabels(); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Printer className="h-5 w-5 text-brand" /> Download shipping labels
            </DialogTitle>
            <DialogDescription>
              All labels are combined into one PDF — one page per order. Orders without an AWB yet, or
              cancelled ones, are skipped.
            </DialogDescription>
          </DialogHeader>

          {!labelJob &&
            (labelPreview ? (
              <div className="space-y-4 text-sm">
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-600">Label size</Label>
                  <div className="grid grid-cols-3 gap-2">
                    {LABEL_SIZES.map((s) => (
                      <Button
                        key={s.key}
                        type="button"
                        variant={labelSize === s.key ? "default" : "outline"}
                        size="sm"
                        onClick={() => setLabelSize(s.key)}
                        aria-pressed={labelSize === s.key}
                        data-testid={`admin-b2c-bulk-label-size-${s.key}`}
                      >
                        {s.label}
                      </Button>
                    ))}
                  </div>
                </div>

                <div className="rounded-lg border bg-slate-50 p-3">
                  <p>
                    <span className="font-semibold">{labelPreview.will_process.toLocaleString("en-IN")}</span>{" "}
                    label{labelPreview.will_process === 1 ? "" : "s"} will be generated.
                  </p>
                  {!selectAllMatching && selectedCount > labelPreview.matched && (
                    <p className="mt-1 text-slate-500">
                      {(selectedCount - labelPreview.matched).toLocaleString("en-IN")} selected order
                      {selectedCount - labelPreview.matched === 1 ? " has" : "s have"} no AWB yet or{" "}
                      {selectedCount - labelPreview.matched === 1 ? "is" : "are"} cancelled — skipped.
                    </p>
                  )}
                  {labelPreview.capped_out > 0 && (
                    <p className="mt-1 flex items-start gap-1.5 text-amber-700">
                      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                      <span>
                        {labelPreview.matched.toLocaleString("en-IN")} match, but up to {labelPreview.cap} labels per
                        download. Narrow the filters or re-run for the remaining{" "}
                        {labelPreview.capped_out.toLocaleString("en-IN")}.
                      </span>
                    </p>
                  )}
                  {labelPreview.will_process === 0 && (
                    <p className="mt-1 text-slate-500">None of the selected orders has an AWB yet — nothing to print.</p>
                  )}
                  {labelPreview.will_process > 0 && labelPreview.will_process < MIN_BULK_LABELS && (
                    <p className="mt-1 flex items-start gap-1.5 text-amber-700">
                      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                      <span>
                        Bulk download needs at least {MIN_BULK_LABELS} printable labels. Select more orders, or download
                        this one from its order page.
                      </span>
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2 py-4 text-sm text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" /> Checking selection…
              </div>
            ))}

          {labelJob && (
            <div className="space-y-3 text-sm">
              <Progress value={labelJob.progress_percentage || 0} />
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
                <span>
                  Processed <b>{labelJob.processed_orders}/{labelJob.total_orders}</b>
                </span>
                <span className="flex items-center gap-1 text-emerald-700">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Generated <b>{labelJob.generated_orders}</b>
                </span>
                <span className="flex items-center gap-1 text-rose-700">
                  <XCircle className="h-3.5 w-3.5" /> Failed <b>{labelJob.failed_orders}</b>
                </span>
                {labelJob.skipped_orders > 0 && (
                  <span className="text-slate-500">
                    Skipped <b>{labelJob.skipped_orders}</b>
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-500">
                <span className="capitalize">Status: {String(labelJob.status).toLowerCase()}</span>
                {!labelDone && labelJob.processed_orders === labelJob.total_orders && labelJob.total_orders > 0 && (
                  <> — building the PDF…</>
                )}
              </p>
              {labelJob.error_message && <p className="text-xs text-rose-700">{labelJob.error_message}</p>}
              {labelJob.error_summary?.length > 0 && (
                <div className="max-h-40 overflow-auto rounded-lg border bg-rose-50/60 p-3">
                  <p className="mb-1 font-medium text-rose-800">Failure reasons</p>
                  <ul className="list-disc space-y-0.5 pl-4 text-xs text-rose-700">
                    {labelJob.error_summary.map((e, i) => (
                      <li key={i}>{e}</li>
                    ))}
                  </ul>
                </div>
              )}
              {labelDone && !labelJob.download_url && !labelJob.error_message && (
                <p className="text-xs text-slate-500">No labels were generated, so there is nothing to download.</p>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            {!labelJob && (
              <>
                <Button variant="outline" onClick={closeLabels} disabled={labelBusy}>
                  Cancel
                </Button>
                <Button
                  onClick={startBulkLabels}
                  disabled={labelBusy || !labelPreview || labelPreview.will_process < MIN_BULK_LABELS}
                  data-testid="admin-b2c-bulk-labels-confirm"
                >
                  {labelBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Printer className="mr-2 h-4 w-4" />}
                  Generate {labelPreview ? labelPreview.will_process.toLocaleString("en-IN") : ""} label
                  {labelPreview?.will_process === 1 ? "" : "s"}
                </Button>
              </>
            )}
            {labelJob && !labelDone && (
              <Button variant="outline" onClick={cancelBulkLabels}>
                Stop
              </Button>
            )}
            {labelDone && (
              <>
                <Button variant="outline" onClick={closeLabels} data-testid="admin-b2c-bulk-labels-done">
                  Done
                </Button>
                {labelJob.download_url && (
                  <Button asChild data-testid="admin-b2c-bulk-labels-download">
                    <a href={labelJob.download_url} target="_blank" rel="noopener noreferrer" download={labelJob.file_name}>
                      <Download className="mr-2 h-4 w-4" />
                      Download PDF
                    </a>
                  </Button>
                )}
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Recently generated bulk-label files */}
      <Dialog open={recentOpen} onOpenChange={setRecentOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-brand" /> Recent bulk shipping label downloads
            </DialogTitle>
            <DialogDescription>
              The last 20 bulk label files, newest first. Download links are refreshed each time this list loads.
            </DialogDescription>
          </DialogHeader>

          <div className="flex justify-end">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => fetchRecentLabels()}
              disabled={recentLoading}
              data-testid="admin-b2c-recent-labels-refresh"
            >
              <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${recentLoading ? "animate-spin" : ""}`} /> Refresh
            </Button>
          </div>

          {recentLoading && recentJobs.length === 0 ? (
            <div className="flex items-center gap-2 py-6 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : recentJobs.length === 0 ? (
            <EmptyState
              icon={Printer}
              title="No bulk label downloads yet"
              description="Select orders on any tab except New and use “Download labels”."
            />
          ) : (
            <ul className="max-h-[60vh] divide-y divide-slate-100 overflow-y-auto rounded-lg border" data-testid="admin-b2c-recent-labels-list">
              {recentJobs.map((j) => {
                const active = !["COMPLETED", "FAILED", "CANCELLED"].includes(j.status);
                return (
                  <li key={j.id} className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0 space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium text-slate-800">
                          {(j.generated_orders ?? 0).toLocaleString("en-IN")} label{j.generated_orders === 1 ? "" : "s"}
                        </span>
                        <span className="rounded border border-slate-200 px-1.5 py-0.5 text-xs font-medium text-slate-600">
                          {LABEL_SIZES.find((s) => s.key === j.size)?.label || j.size}
                        </span>
                        <StatusBadge status={j.status} kind="job" size="sm" />
                      </div>
                      <p className="text-xs text-slate-500">
                        {formatToIST(j.created_at)}
                        {j.initiated_by_name ? ` · by ${j.initiated_by_name}` : ""}
                      </p>
                      {(j.failed_orders > 0 || j.skipped_orders > 0 || j.capped_out > 0) && (
                        <p className="text-xs text-slate-500">
                          {[
                            j.failed_orders > 0 && `${j.failed_orders} failed`,
                            j.skipped_orders > 0 && `${j.skipped_orders} skipped`,
                            j.capped_out > 0 && `${j.capped_out} over the cap`,
                          ].filter(Boolean).join(" · ")}
                        </p>
                      )}
                      {active && (
                        <p className="text-xs text-blue-700">
                          {j.processed_orders ?? 0}/{j.total_orders ?? 0} processed ({j.progress_percentage || 0}%)
                        </p>
                      )}
                      {j.status === "FAILED" && j.error_message && (
                        <p className="text-xs text-rose-700">{j.error_message}</p>
                      )}
                    </div>
                    {j.download_url ? (
                      <Button asChild size="sm" variant="outline" className="shrink-0" data-testid={`admin-b2c-recent-label-download-${j.id}`}>
                        <a href={j.download_url} target="_blank" rel="noopener noreferrer" download={j.file_name}>
                          <Download className="mr-1.5 h-4 w-4" /> Download PDF
                        </a>
                      </Button>
                    ) : active ? (
                      <span className="inline-flex shrink-0 items-center gap-1.5 text-xs text-slate-500">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Generating…
                      </span>
                    ) : (
                      <span className="shrink-0 text-xs text-slate-400">No file</span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default B2COrders;
